// @flow

import React, { useState } from 'react';

import { useTrackingContext } from '@zapier/tracking';
import { getCookie, setCookie } from '@zapier/common-utils';

import PolicyModal from './PolicyModal';
import usePolicyData from './usePolicyData';

const SNOOZE_COOKIE_PREFIX = 'policySnooze';
const SNOOZE_COOKIE_EXP = 60 * 60;

const EVENT_POLICY_PROMPT_SHOWN = 'policy_shown';
const EVENT_POLICY_AGREED = 'policy_agreement';

const snoozeCookieMonster = slug => `${SNOOZE_COOKIE_PREFIX}_${slug}`;

const silenceLogger = (slug: string, reasons: string): void => {
  console.info(`Silencing pending policy \`${slug}\` because ${reasons}.`);
};

type Props = {
  /** Current pathname of page to exempt policies that apply. */
  currentPathname: string,
};

const PolicyPendingCheck = ({ currentPathname }: Props) => {
  const [, forceUpdate] = useState();
  const { policies: pendingPolicies } = usePolicyData({
    pending: true,
    first: 1,
    onMutationError: () => {
      const { slug } = pendingPolicies[0];
      setCookie(
        snoozeCookieMonster(slug),
        'from_accept_err',
        SNOOZE_COOKIE_EXP
      );
      forceUpdate({});
    },
  });

  const { trackEvent } = useTrackingContext();

  if (!pendingPolicies.length) {
    return null;
  }

  const {
    title,
    contentId: content,
    slug,
    tldr,
    onAccept,
  } = pendingPolicies[0];

  // Exempt the modal for the policy's own page.
  if (currentPathname === slug) {
    silenceLogger(slug, 'pathname');
    return null;
  }

  const hasSnoozeCookie = Boolean(getCookie(snoozeCookieMonster(slug)));
  if (hasSnoozeCookie) {
    silenceLogger(slug, 'snooze is on');
    return null;
  }

  const eventParams = { policy_slug: slug, policy_content: content };
  trackEvent(EVENT_POLICY_PROMPT_SHOWN, eventParams);

  const onAcceptHandler = () => {
    trackEvent(EVENT_POLICY_AGREED, eventParams);
    return onAccept();
  };

  return (
    <PolicyModal
      onAccept={onAcceptHandler}
      title={title}
      tldr={tldr}
      url={`https://zapier.com/${slug}`}
    ></PolicyModal>
  );
};

export default PolicyPendingCheck;
